import { useState } from 'react'
import {
  Shield,
  Skull,
  Trophy,
  XCircle,
  Zap,
  Sparkles,
  ShieldAlert,
  Crosshair,
  Flame,
  Swords,
  ChevronDown,
  Building2,
} from 'lucide-react'
import { UNIT_META, RESOURCE_CONFIG } from '@territory-nexus/shared'
import clsx from 'clsx'
import { UnitSprite } from './sprites'

// ─── Shared highlight icon helper ────────────────────────────
export function getHighlightIcon(type: string, size = 'w-3 h-3') {
  switch (type) {
    case 'kill': return <Skull className={`${size} text-red-400`} />
    case 'crit': return <Zap className={`${size} text-amber-400`} />
    case 'turret_hit': return <ShieldAlert className={`${size} text-orange-400`} />
    case 'ability': return <Sparkles className={`${size} text-purple-400`} />
    case 'structure_hit': return <Crosshair className={`${size} text-cyan-400`} />
    case 'structure_destroyed': return <Flame className={`${size} text-orange-400`} />
    default: return <Swords className={`${size} text-slate-400`} />
  }
}

// ─── Types ───────────────────────────────────────────────────
interface ResultUnit {
  id: string
  unitType: string
  currentHp: number
  maxHp: number
}

interface ResultStructure {
  id: string
  structureType: string
  level: number
  currentHp: number
  maxHp: number
}

interface BattleResultOverlayProps {
  victory: boolean
  turns: number
  battleType: 'pve' | 'pvp'
  playerUnits: ResultUnit[]
  enemyUnits?: ResultUnit[]
  structures?: ResultStructure[]
  highlights: { turn: number; type: string; description: string }[]
  rewards?: { resources: Record<string, number> } | null
  modDrop?: { rarity: string; modType: string }
  onContinue: () => void
}

const STRUCTURE_LABELS: Record<string, string> = {
  laser_turret: 'Laser Turret',
  shield_generator: 'Shield Gen',
  stealth_field: 'Stealth Field',
  headquarters: 'HQ',
  mining_rig: 'Mining Rig',
  research_lab: 'Research Lab',
  teleport_gate: 'Teleport Gate',
}

// ─── Unit HP Row ─────────────────────────────────────────────
function UnitHpRow({ unit, corrupted }: { unit: ResultUnit; corrupted?: boolean }) {
  const meta = UNIT_META[unit.unitType as keyof typeof UNIT_META]
  const alive = unit.currentHp > 0
  const hpPct = Math.max(0, (unit.currentHp / unit.maxHp) * 100)

  return (
    <div className={clsx('flex items-center gap-2 p-1.5 rounded', !alive && 'opacity-40')}>
      <UnitSprite
        unitType={unit.unitType as any}
        level={1}
        status={corrupted ? 'deployed' : 'idle'}
        size="sm"
        showLevel={false}
        animate={false}
        corrupted={corrupted}
      />
      <span className={clsx('text-xs font-medium w-16 truncate', corrupted ? 'text-purple-400' : (meta?.color || 'text-white'))}>
        {meta?.shortName || unit.unitType}
      </span>
      <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div
          className={clsx(
            'h-full rounded-full',
            corrupted
              ? (hpPct > 50 ? 'bg-purple-400' : hpPct > 25 ? 'bg-red-400' : 'bg-red-600')
              : (hpPct > 50 ? 'bg-emerald-400' : hpPct > 25 ? 'bg-amber-400' : 'bg-red-400')
          )}
          style={{ width: `${hpPct}%` }}
        />
      </div>
      <span className="text-[10px] text-slate-400 w-10 text-right">
        {alive ? `${unit.currentHp}/${unit.maxHp}` : 'KIA'}
      </span>
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────
export default function BattleResultOverlay({
  victory,
  turns,
  battleType,
  playerUnits,
  enemyUnits,
  structures,
  highlights,
  rewards,
  modDrop,
  onContinue,
}: BattleResultOverlayProps) {
  const [showHighlights, setShowHighlights] = useState(false)
  const isPvE = battleType === 'pve'

  return (
    <div className="absolute inset-0 z-50 bg-slate-950/95 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 pb-24 animate-fade-in">
      <div className="glass rounded-xl border border-white/10 p-6 max-w-sm w-full space-y-4 mt-auto mb-auto animate-scale-in">
        {/* Victory / Defeat Header */}
        <div className="text-center">
          {victory ? (
            <>
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                {isPvE
                  ? <Shield className="w-8 h-8 text-emerald-400" />
                  : <Trophy className="w-8 h-8 text-amber-400" />
                }
              </div>
              <h3 className="text-lg font-bold text-emerald-400">
                {isPvE ? 'Zone Purged!' : 'Victory!'}
              </h3>
              <p className="text-sm text-slate-400 mt-1">
                {isPvE
                  ? `Corruption eliminated in ${turns} turns`
                  : `Zone conquered in ${turns} turns`
                }
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-3">
                {isPvE
                  ? <Skull className="w-8 h-8 text-red-400" />
                  : <XCircle className="w-8 h-8 text-red-400" />
                }
              </div>
              <h3 className="text-lg font-bold text-red-400">Defeated</h3>
              <p className="text-sm text-slate-400 mt-1">
                {isPvE
                  ? 'The corruption was too strong...'
                  : `Your forces were repelled after ${turns} turns`
                }
              </p>
            </>
          )}

          {/* Battle Type Badge */}
          <div className="mt-3">
            <span className={clsx(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border',
              isPvE
                ? 'bg-orange-500/20 text-orange-400 border-orange-500/30'
                : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
            )}>
              {isPvE ? 'PvE' : 'PvP'}
            </span>
          </div>
        </div>

        {/* Your Units */}
        <div className="space-y-1.5">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Your Units</p>
          {playerUnits.map(u => (
            <UnitHpRow key={u.id} unit={u} />
          ))}
        </div>

        {/* Enemy Units */}
        {enemyUnits && enemyUnits.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] text-red-400/70 uppercase tracking-wider">
              {isPvE ? 'Corrupted Units' : 'Defender Units'}
            </p>
            {enemyUnits.map(u => (
              <UnitHpRow key={u.id} unit={u} corrupted={isPvE} />
            ))}
          </div>
        )}

        {/* Defender Structures (PvP) */}
        {structures && structures.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              Defender Structures
            </p>
            {structures.map(s => {
              const alive = s.currentHp > 0
              const hpPct = Math.max(0, (s.currentHp / s.maxHp) * 100)
              return (
                <div key={s.id} className={clsx('flex items-center gap-2 p-1.5 rounded', !alive && 'opacity-40')}>
                  <span className="text-xs font-medium w-24 truncate text-slate-200">
                    {STRUCTURE_LABELS[s.structureType] || s.structureType}
                    <span className="text-slate-500 ml-1">Lv{s.level}</span>
                  </span>
                  <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={clsx('h-full rounded-full', alive ? (hpPct > 50 ? 'bg-blue-400' : hpPct > 25 ? 'bg-amber-400' : 'bg-red-400') : 'bg-red-900')}
                      style={{ width: `${alive ? hpPct : 100}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-slate-400 w-16 text-right">
                    {alive ? `${s.currentHp}/${s.maxHp}` : 'Destroyed'}
                  </span>
                </div>
              )
            })}
          </div>
        )}

        {/* Key Moments */}
        {highlights.length > 0 && (
          <div className="border-t border-white/5 pt-3">
            <button
              onClick={() => setShowHighlights(!showHighlights)}
              className="w-full flex items-center justify-between mb-1.5"
            >
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">
                Key Moments ({highlights.length})
              </p>
              <ChevronDown className={clsx('w-3.5 h-3.5 text-slate-500 transition-transform', showHighlights && 'rotate-180')} />
            </button>
            {showHighlights && (
              <div className="space-y-1 max-h-36 overflow-y-auto">
                {highlights
                  .filter(h => h.type === 'kill' || h.type === 'crit' || h.type === 'turret_hit' || h.type === 'structure_hit' || h.type === 'structure_destroyed')
                  .slice(0, 10)
                  .map((h, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-[11px]">
                      <span className="mt-0.5 flex-shrink-0">{getHighlightIcon(h.type)}</span>
                      <span className="text-slate-300">
                        <span className="text-slate-500">T{h.turn}</span> {h.description}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Rewards (PvE) */}
        {victory && rewards && (
          <div className="border-t border-white/5 pt-3">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Rewards</p>
            <div className="flex items-center gap-3">
              {Object.entries(rewards.resources).map(([key, val]) => {
                if (val <= 0) return null
                const rc = RESOURCE_CONFIG[key as keyof typeof RESOURCE_CONFIG]
                if (!rc) return null
                return (
                  <span key={key} className={`text-xs font-medium ${rc.color}`}>
                    {rc.symbol}+{val}
                  </span>
                )
              })}
            </div>
          </div>
        )}

        {/* Mod Drop */}
        {modDrop && (
          <div className="border-t border-white/5 pt-3 text-center">
            <Sparkles className="w-5 h-5 text-purple-400 mx-auto mb-1" />
            <p className="text-xs font-medium text-purple-300">
              Mod Drop: {modDrop.rarity} {modDrop.modType}
            </p>
          </div>
        )}

        {/* Continue Button */}
        <button
          onClick={onContinue}
          className="w-full py-2.5 rounded-lg text-sm font-semibold bg-white/10 hover:bg-white/20 text-white transition"
        >
          Continue
        </button>
      </div>
    </div>
  )
}
